import { useState } from "react";
import { Link } from "react-router-dom";
import { type AdminUserDetail, type OrgRole } from "@currencycore/shared";
import { ApiRequestError, banUser, deleteUser, getUser, setMemberRole, unbanUser } from "../lib/api.ts";
import { Select } from "./Select.tsx";
import { PlanBadge } from "./Badges.tsx";
import { useConfirm } from "./useConfirm.tsx";
import { toast } from "./Toast.tsx";
import { errMsg, fmtDate } from "../lib/ui.ts";

const ROLE_OPTIONS = [
  { value: "owner", label: "Owner" },
  { value: "admin", label: "Admin" },
  { value: "member", label: "Member" },
];

/**
 * Shared user detail body (used by the user drawer + the full user page):
 * info, ban/unban, per-org role editing (owner-locked) with org cross-links,
 * and delete. All mutating actions confirm first.
 */
export function UserDetailBody({
  detail,
  onUpdated,
  onDeleted,
}: {
  detail: AdminUserDetail;
  onUpdated: (d: AdminUserDetail) => void;
  onDeleted: () => void;
}) {
  const [reason, setReason] = useState("");
  const [working, setWorking] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const [blockingOrgs, setBlockingOrgs] = useState<{ id: string; name: string }[]>([]);
  const { ask, dialog } = useConfirm();

  async function doBan() {
    setWorking(true);
    try {
      const u = await banUser(detail.id, reason);
      onUpdated(u);
      setReason("");
      toast("User banned (login disabled).", true);
    } catch (e) {
      toast(errMsg(e, "Could not ban user."));
    } finally {
      setWorking(false);
    }
  }

  async function doUnban() {
    setWorking(true);
    try {
      const u = await unbanUser(detail.id);
      onUpdated(u);
      toast("User unbanned.", true);
    } catch (e) {
      toast(errMsg(e, "Could not unban user."));
    } finally {
      setWorking(false);
    }
  }

  async function doDelete() {
    setWorking(true);
    setBlockingOrgs([]);
    try {
      const res = await deleteUser(detail.id);
      toast(
        res.deletedOrgs.length
          ? `User deleted (removed ${res.deletedOrgs.length} solo org${res.deletedOrgs.length > 1 ? "s" : ""}).`
          : "User deleted.",
        true,
      );
      onDeleted();
    } catch (e) {
      if (e instanceof ApiRequestError && e.status === 409) {
        const d = e.details as { organizations?: { id: string; name: string }[] } | undefined;
        setBlockingOrgs(d?.organizations ?? []);
        toast("Blocked: user solely owns shared organizations.");
      } else {
        toast(errMsg(e, "Could not delete user."));
      }
    } finally {
      setWorking(false);
    }
  }

  function changeRole(orgId: string, role: OrgRole, current: OrgRole) {
    if (role === current) return;
    const isTransfer = role === "owner";
    ask({
      title: isTransfer ? "Transfer ownership?" : "Change role?",
      message: isTransfer ? (
        <>
          Make <strong>{detail.name ?? detail.email}</strong> the owner of this organization? The
          current owner will be demoted to admin.
        </>
      ) : (
        <>
          Change <strong>{detail.name ?? detail.email}</strong>'s role to <strong>{role}</strong>?
        </>
      ),
      confirmLabel: isTransfer ? "Transfer ownership" : "Change role",
      run: async () => {
        try {
          await setMemberRole(orgId, detail.id, role);
          onUpdated(await getUser(detail.id));
          toast("Role updated.", true);
        } catch (e) {
          toast(errMsg(e, "Could not change role."));
        }
      },
    });
  }

  return (
    <>
      <section className="drawer-section">
        <div className="section-row between" style={{ marginBottom: "12px" }}>
          <h4 style={{ margin: 0 }}>Overview</h4>
          <Link to={`/emails?tab=send&userId=${detail.id}`} className="btn btn-secondary btn-xs">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M22 2L11 13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
            Email user
          </Link>
        </div>
        <div className="kv-grid">
          <div><span className="k">User ID</span><span className="v mono">{detail.id}</span></div>
          <div><span className="k">Email verified</span><span className="v">{detail.emailVerified ? "Yes" : "No"}</span></div>
          <div><span className="k">Created</span><span className="v tnum">{fmtDate(detail.createdAt)}</span></div>
          <div><span className="k">Status</span><span className="v">{detail.banned ? <span className="badge badge-danger">banned</span> : <span className="badge badge-ok">active</span>}</span></div>
        </div>
      </section>

      <section className="drawer-section">
        <h4>Account status</h4>
        {detail.banned ? (
          <>
            <div className="ban-reason">
              <strong>Account banned.</strong>{" "}
              {detail.bannedReason ? <>Reason: {detail.bannedReason}</> : <span className="muted">No reason recorded.</span>}
              {detail.bannedAt && <span className="muted"> · {fmtDate(detail.bannedAt)}</span>}
            </div>
            <button className="btn btn-primary btn-sm" onClick={doUnban} disabled={working}>
              {working ? <><span className="spinner" />…</> : "Unban user"}
            </button>
          </>
        ) : (
          <>
            <p className="hint">Banning disables new logins (login lockout). Existing API keys keep serving traffic.</p>
            <div className="ban-row">
              <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" />
              <button className="btn btn-danger btn-sm" onClick={doBan} disabled={working}>
                {working ? <><span className="spinner" />…</> : "Ban"}
              </button>
            </div>
          </>
        )}
      </section>

      <section className="drawer-section">
        <div className="section-row between">
          <h4>Organizations</h4>
          <span className="muted">{detail.organizations.length}</span>
        </div>
        {detail.organizations.length === 0 ? (
          <p className="muted">Not a member of any organization.</p>
        ) : (
          <div className="member-list">
            {detail.organizations.map((o) => (
              <div className="member-row" key={o.organizationId}>
                <Link to={`/organizations/${o.organizationId}`} className="member-meta linkable">
                  <span className="name">{o.organizationName}</span>
                  <span className="email mono muted">{o.organizationId}</span>
                </Link>
                <PlanBadge plan={o.plan} />
                {o.role === "owner" ? (
                  <span className="role-locked" title="Transfer ownership to change">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="5" y="11" width="14" height="10" rx="2" />
                      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                    </svg>
                    Owner
                  </span>
                ) : (
                  <Select value={o.role} options={ROLE_OPTIONS} onChange={(v) => changeRole(o.organizationId, v as OrgRole, o.role)} ariaLabel={`Role in ${o.organizationName}`} />
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="drawer-section danger-zone">
        <h4>Danger zone</h4>
        {blockingOrgs.length > 0 && (
          <div className="error-banner">
            Can’t delete: solely owns {blockingOrgs.map((o) => o.name).join(", ")}. Transfer ownership first.
          </div>
        )}
        {!showDelete ? (
          <button className="btn btn-danger btn-sm" onClick={() => setShowDelete(true)}>Delete user…</button>
        ) : (
          <>
            <p className="hint">Type <strong>delete</strong> to confirm. Solo-owned orgs are removed with the account.</p>
            <div className="ban-row">
              <input className="input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="delete" />
              <button className="btn btn-danger btn-sm" onClick={doDelete} disabled={working || confirmText !== "delete"}>
                {working ? <><span className="spinner" />…</> : "Delete"}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => { setShowDelete(false); setConfirmText(""); }}>Cancel</button>
            </div>
          </>
        )}
      </section>

      {dialog}
    </>
  );
}
